"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AssignmentEntry = void 0;
const lodash_1 = require("lodash");
const assignment_1 = require("../assignment");
const common_1 = require("../common");
const AssignmentEntryUpdateData_1 = require("./AssignmentEntryUpdateData");
const PinEntry_1 = require("./PinEntry");
var AssignmentEntry;
(function (AssignmentEntry) {
    AssignmentEntry.excludedComparisonPaths = ['createdAt', 'modifiedAt', 'migratedAt'];
    AssignmentEntry.maxRegisteredLoginCount = 50;
    function createIds(assignmentIds, userIds) {
        return (0, lodash_1.flatMap)(assignmentIds, (assignmentId) => userIds.map((userId) => createId(assignmentId, userId)));
    }
    AssignmentEntry.createIds = createIds;
    function createId(assignmentId, userId) {
        return `${assignmentId}-${userId}`;
    }
    AssignmentEntry.createId = createId;
    function splitIdIntoComponents(entryId) {
        const [assignmentId, userId] = entryId.split('-');
        return { assignmentId, userId };
    }
    AssignmentEntry.splitIdIntoComponents = splitIdIntoComponents;
    function create(meta, user, schedulable) {
        const testData = assignment_1.AssignmentMeta.isTest(meta)
            ? {
                phase: 'created',
            }
            : undefined;
        const now = Date.now();
        return {
            _id: createId(meta._id, user._id),
            createdAt: now,
            type: meta.type,
            modifiedAt: now,
            assignmentId: meta._id,
            userId: user._id,
            displayName: user.displayName ?? undefined,
            familyName: user.familyName ?? undefined,
            totalPins: meta.assignmentInfo.numberOfPins,
            entries: {},
            status: 'default',
            testData,
            ...schedulable,
        };
    }
    AssignmentEntry.create = create;
    /** creates a clone of the given entry and applies the given update logic so we can use it for e.g. comparisons */
    function update(assignmentEntry, updates) {
        const cloned = (0, lodash_1.cloneDeep)(assignmentEntry);
        const updateFields = AssignmentEntryUpdateData_1.AssignmentEntryUpdateData.updateFields(updates);
        (0, lodash_1.forEach)(updateFields, (value, field) => (0, lodash_1.set)(cloned, field, value));
        return cloned;
    }
    AssignmentEntry.update = update;
    function createForUsers(meta, users) {
        return (0, lodash_1.map)(users, (user) => AssignmentEntry.create(meta, user));
    }
    AssignmentEntry.createForUsers = createForUsers;
    function progress(entry) {
        const done = doneCount(entry);
        return common_1.Progress.calc(done, entry.totalPins);
    }
    AssignmentEntry.progress = progress;
    function doneCount(entry, onlyInteractive = false) {
        return (0, lodash_1.values)(entry.entries)
            .filter((pinEntry) => (onlyInteractive ? PinEntry_1.PinEntry.isInteractive(pinEntry) : true))
            .filter((pinEntry) => pinEntry && pinEntry.done === true).length;
    }
    AssignmentEntry.doneCount = doneCount;
    function pinEntries(assignmentEntry) {
        return (0, lodash_1.compact)((0, lodash_1.values)(assignmentEntry.entries));
    }
    AssignmentEntry.pinEntries = pinEntries;
    function pinEntryFromEntries(entries, pinId) {
        if ((0, lodash_1.isNil)(pinId)) {
            return [];
        }
        return (0, lodash_1.compact)((0, lodash_1.map)(entries, (value) => AssignmentEntry.entryForPin(value, pinId)));
    }
    AssignmentEntry.pinEntryFromEntries = pinEntryFromEntries;
    function pinEntriesWithDisplayNameFromEntries(entries, pinId) {
        if ((0, lodash_1.isNil)(pinId)) {
            return [];
        }
        return (0, lodash_1.compact)(entries.map((value) => {
            const entry = entryForPin(value, pinId);
            if (!entry)
                return undefined;
            if (!hasDisplayName(value))
                return undefined;
            return {
                ...entry,
                displayName: value.displayName,
            };
        }));
    }
    AssignmentEntry.pinEntriesWithDisplayNameFromEntries = pinEntriesWithDisplayNameFromEntries;
    function entryForPin(entry, pinId) {
        return pinId ? (0, lodash_1.get)(entry && entry.entries, pinId) : undefined;
    }
    AssignmentEntry.entryForPin = entryForPin;
    function answerForPinEntry(pinEntry) {
        if (!pinEntry) {
            return undefined;
        }
        return pinEntry.answer;
    }
    AssignmentEntry.answerForPinEntry = answerForPinEntry;
    function answerForPin(entry, pinId) {
        const pinEntry = entryForPin(entry, pinId);
        if (!pinEntry) {
            return undefined;
        }
        return pinEntry.answer;
    }
    AssignmentEntry.answerForPin = answerForPin;
    function resultForPin(entry, pinId) {
        const pinEntry = entryForPin(entry, pinId);
        if (!pinEntry) {
            return undefined;
        }
        return pinEntry.result;
    }
    AssignmentEntry.resultForPin = resultForPin;
    function hasStatus(entry, status) {
        return entry?.status === status;
    }
    AssignmentEntry.hasStatus = hasStatus;
    /** returns entries that match one of the given statusses */
    function filterByStatus(entries, status) {
        const statuses = Array.isArray(status) ? status : [status];
        return entries.filter((entry) => statuses.includes(entry.status));
    }
    AssignmentEntry.filterByStatus = filterByStatus;
    function isAnyPinSeen(entry) {
        return Object.values(entry.entries).some((e) => !!(e && e.seen));
    }
    AssignmentEntry.isAnyPinSeen = isAnyPinSeen;
    function countPinSeen(entry) {
        return Object.values(entry.entries).filter((e) => e && e.seen).length;
    }
    AssignmentEntry.countPinSeen = countPinSeen;
    function calcPercentagePinsDone(entry) {
        const total = entry.totalPins;
        const count = Object.values(entry.entries).filter((e) => e && e.done).length;
        if (total == 0)
            return 0;
        return (count / total) * 100;
    }
    AssignmentEntry.calcPercentagePinsDone = calcPercentagePinsDone;
    function timeSpentOnPin(entry, pinId) {
        const pinEntry = entryForPin(entry, pinId);
        return (pinEntry && pinEntry.timeSpent) || 0;
    }
    AssignmentEntry.timeSpentOnPin = timeSpentOnPin;
    function isTestPhase(entry, phase) {
        return entry.testData?.phase === phase;
    }
    AssignmentEntry.isTestPhase = isTestPhase;
    function unansweredInteractivePinCount(entry, assignment) {
        const pinsWithPoints = assignment_1.AssignmentUtils.pinsWithTestPoints(assignment);
        return (0, lodash_1.filter)(pinsWithPoints, (p) => {
            if (!assignment_1.AssignmentUtils.pinIsInAssignment(p, assignment)) {
                return false;
            }
            const pinEntry = AssignmentEntry.entryForPin(entry, p._id);
            return !pinEntry || !pinEntry.done;
        }).length;
    }
    AssignmentEntry.unansweredInteractivePinCount = unansweredInteractivePinCount;
    function getGrade(entry) {
        const grade = entry.testData && entry.testData.grade;
        if (grade == undefined)
            return undefined;
        if ((0, lodash_1.isNumber)(grade))
            return grade;
        const num = Number(grade);
        return (0, lodash_1.isFinite)(num) ? num : grade;
    }
    AssignmentEntry.getGrade = getGrade;
    function isOneOfTestPhases(entry, phases) {
        return entry.testData && phases.includes(entry.testData.phase);
    }
    AssignmentEntry.isOneOfTestPhases = isOneOfTestPhases;
    function countPhases(entries) {
        const phases = (0, lodash_1.compact)(entries.map((entry) => {
            return entry.status === 'default' && entry.testData && entry.testData.phase;
        }));
        return common_1.TestPhase.countPhases(phases);
    }
    AssignmentEntry.countPhases = countPhases;
    function hasSeenPin(entry, pinId) {
        const pinEntry = entryForPin(entry, pinId);
        return !!pinEntry?.seen;
    }
    AssignmentEntry.hasSeenPin = hasSeenPin;
    function hasUnseenComments(entry) {
        return (0, lodash_1.values)(entry.entries).some((pinEntry) => pinEntry && PinEntry_1.PinEntry.hasUnseenComment(pinEntry));
    }
    AssignmentEntry.hasUnseenComments = hasUnseenComments;
    function hasUnseenCommentForPin(entry, pinId) {
        const pinEntry = entryForPin(entry, pinId);
        return pinEntry && PinEntry_1.PinEntry.hasUnseenComment(pinEntry);
    }
    AssignmentEntry.hasUnseenCommentForPin = hasUnseenCommentForPin;
    function highestTestPhase(entries) {
        const highestPhaseEntry = (0, lodash_1.maxBy)(entries, (entry) => {
            return entry.testData ? common_1.TestPhase.orderIndex(entry.testData.phase) : 0;
        });
        return highestPhaseEntry && highestPhaseEntry.testData && highestPhaseEntry.testData.phase;
    }
    AssignmentEntry.highestTestPhase = highestTestPhase;
    function containsAnyTestPhase(entries, testPhases) {
        return (0, lodash_1.some)(entries, (entry) => (0, lodash_1.some)(testPhases, (testPhase) => AssignmentEntry.isTestPhase(entry, testPhase)));
    }
    AssignmentEntry.containsAnyTestPhase = containsAnyTestPhase;
    function isKicked(entry) {
        return hasStatus(entry, 'kicked');
    }
    AssignmentEntry.isKicked = isKicked;
    function hasDisplayName(entry) {
        return Boolean(entry.displayName && entry.displayName.length);
    }
    AssignmentEntry.hasDisplayName = hasDisplayName;
    function isActiveEntry(entry) {
        return hasStatus(entry, 'default');
    }
    AssignmentEntry.isActiveEntry = isActiveEntry;
    function shouldHaveBookmark(entry) {
        // Any entry should have a bookmark, except when the match the following criteria.
        return !isKicked(entry);
    }
    AssignmentEntry.shouldHaveBookmark = shouldHaveBookmark;
    // TODO: Re-enable if necessary
    // export function shuffleMapForQuestion(
    //   pin: AssignmentPin<QuizQuestion.AssignmentContent>,
    //   userId: string
    // ): QuizQuestion.AnswerValue[] {
    //   const options = pin.item.custom.answers.map((a) => a.value);
    //   const map = SeedShuffle.shuffle(options, `${userId}-${pin._id}`);
    //   return map;
    // }
    function orderedTestPins(entry, assignment) {
        const pinsForAssignmentType = assignment_1.AssignmentUtils.pinsByAssignmentType(assignment);
        return assignment_1.AssignmentUtils.pinsOrShuffledPinsForAssignment(assignment, pinsForAssignmentType, entry.userId);
    }
    function pinIsInFuture(entry, assignment, pinId) {
        if (!assignment_1.AssignmentSettings.assignmentIsLinear(assignment.settings)) {
            return undefined;
        }
        const assignmentPins = orderedTestPins(entry, assignment);
        if (!assignmentPins.length)
            return false;
        const activePinIndex = assignmentPins.findIndex((pin) => pin._id === entry.activePinId);
        const futurePinIndex = assignmentPins.findIndex((pin) => pin._id === pinId);
        return activePinIndex === -1 || activePinIndex < futurePinIndex;
    }
    AssignmentEntry.pinIsInFuture = pinIsInFuture;
    function pinIsInThePast(entry, assignment, pinId) {
        if (!assignment_1.AssignmentSettings.assignmentIsLinear(assignment.settings)) {
            return false;
        }
        const assignmentPins = orderedTestPins(entry, assignment);
        if (!assignmentPins.length)
            return false;
        const activePinIndex = assignmentPins.findIndex((pin) => pin._id === entry.activePinId);
        const pastPin = assignmentPins.findIndex((pin) => pin._id === pinId);
        return pastPin < activePinIndex;
    }
    AssignmentEntry.pinIsInThePast = pinIsInThePast;
    function raisedHandsWithName(entries) {
        const list = (0, lodash_1.compact)(entries.map((entry) => {
            if (!entry.raiseHand)
                return;
            return {
                ...entry.raiseHand,
                user: entry.userId,
                displayName: entry.displayName || '',
            };
        }));
        return (0, lodash_1.sortBy)(list, 'timeStamp');
    }
    AssignmentEntry.raisedHandsWithName = raisedHandsWithName;
    function loginCountDuringTest(entry) {
        return entry?.logins?.length || 0;
    }
    AssignmentEntry.loginCountDuringTest = loginCountDuringTest;
})(AssignmentEntry || (exports.AssignmentEntry = AssignmentEntry = {}));
//# sourceMappingURL=AssignmentEntry.js.map